import { useEffect, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { Guard } from '../App';
import { readStepUpMethod, type StepUpMethod } from '../lib/auth';

/**
 * The owner's choice of how protected actions are confirmed. Choosing the password is itself
 * confirmed with the authenticator once; going back to the authenticator never needs it.
 */
export function StepUpMethodSetting({ client, guard }: { client: SupabaseClient; guard: Guard }): ReactElement {
  const [method, setMethod] = useState<StepUpMethod | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void readStepUpMethod(client).then((value) => { if (live) setMethod(value); });
    return () => { live = false; };
  }, [client]);

  const choose = (next: StepUpMethod) => {
    if (next === method || busy) return;
    setMessage(null);
    void guard('post_owner_command', async () => {
      setBusy(true);
      try {
        const { data, error } = await client.rpc('set_owner_step_up_method', { request: { method: next } });
        const reply = data as { ok?: boolean; error?: { code?: string } } | null;
        if (error || reply?.ok !== true) {
          setMessage(reply?.error?.code === 'step_up_required'
            ? 'Confirm with your authenticator code once to switch to the password.'
            : 'The setting could not be saved.');
          return;
        }
        setMethod(await readStepUpMethod(client));
      } finally {
        setBusy(false);
      }
    });
  };

  return (
    <div className="stack">
      <div className="row" role="group" aria-label="Confirm protected actions with">
        <small className="muted">Confirm protected actions with</small>
        <button type="button" className={method === 'authenticator' ? 'primary' : 'ghost'} aria-pressed={method === 'authenticator'}
          disabled={busy || method === null} onClick={() => choose('authenticator')}>Authenticator code</button>
        <button type="button" className={method === 'password' ? 'primary' : 'ghost'} aria-pressed={method === 'password'}
          disabled={busy || method === null} onClick={() => choose('password')}>Password</button>
      </div>
      <small className="muted">
        {method === 'password'
          ? 'Your password covers protected actions for 15 minutes after you enter it. The authenticator still works too.'
          : 'Each session asks for the 6-digit code from your authenticator app before a protected action.'}
      </small>
      {message && <small role="alert">{message}</small>}
    </div>
  );
}
